import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useNavigate, useParams, Navigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Header, Footer, ABC_TRUSTPILOT } from '../Common';
import SecondaryBanner from '@ajat/design-system/ContentBlocks/SecondaryBanner';
import schoolShopBanner from '../../Common/SchoolShop/schoolShopBanner';
import resolveHeaderImages from '../../Common/SchoolShop/headerImages';
import FomoBlock from '@ajat/design-system/ContentBlocks/FomoBlock';
import ProductsGrid from '@ajat/design-system/ContentBlocks/ProductsGrid';
import Fullwidth from '@ajat/design-system/ContentBlocks/Fullwidth';
import Preloader from '@ajat/design-system/Preloader';
import { usePageLoader } from '@ajat/design-system/PageLoader';
import ShopEmptyState from '../../Common/SchoolShop/ShopEmptyState';
import SchoolSwitchPopup from '../../Common/SchoolShop/SchoolSwitchPopup';
import useSchoolVariations from '../../../../hooks/useSchoolVariations';
import ClosedShopMessage from '../../Common/ClosedShop/ClosedShopMessage';
import useSchoolClass from '../../../../hooks/useSchoolClass';
import useSchools from '../../../../hooks/useSchools';
import useBrandSettings from '../../../../hooks/useBrandSettings';
import { useCart } from '../../../../context/CartContext';
import { pickGroup } from '../../../../services/menuDestination';
import {
  getSchoolSelection,
  setSchoolSelection,
} from '../../../../services/schoolSession';
import productShortDescription from '../../../../utils/shortDescription';
import {
  SKOLMERCH_BANNER,
  BANNER_IMAGES,
  FOMO_COUNTDOWN_TEMPLATE,
  FOMO_DATE_TEMPLATE,
  FOMO_UNITS,
  PACKAGE_DESCRIPTION,
} from './schoolShopDisplay';
import '../../Common/SchoolShop/SchoolShop.css';
import { discountCardFields } from '../../../../services/discountBadge';

const SCHOOL_KEY = 'abcse_school_selection';
// Brand slug used by all ABC-SE storefront API calls.
const BRAND_SLUG = 'abcse';

/**
 * Convert a backend variation row into a ProductCard `items` entry.
 * Color chips come from the canonical `color` slug attribute only — other
 * color-typed attributes (e.g. `logo-color`) are intentionally excluded.
 */
const variationToCardItem = (variation, schoolSlug, shopSlug, navigate) => {
  const pickColorValues = (list) => (list ?? [])
    .filter((a) => a.slug === 'color')
    .flatMap((a) => a.values.map((v) => v.rawValue ?? v.value));

  const pinnedColors = pickColorValues(variation.pinned_attributes);
  const optionColors = pickColorValues(variation.available_options);

  const imageList = Array.isArray(variation.images) ? variation.images : [];
  const firstImage = imageList[0]?.src;
  const secondImage = imageList[1]?.src;

  return {
    id: variation.id,
    image: firstImage,
    imageAlt: variation.name,
    secondImage,
    secondImageAlt: variation.name,
    title: variation.name,
    description: productShortDescription(variation),
    price: variation.price,
    ...discountCardFields(variation, 'abc-se'),
    colors: pinnedColors.length ? pinnedColors : optionColors,
    // Carry the shop slug so the exact shop's overrides resolve in the configurator.
    onClick: () => navigate(
      `/clothing/abc-se/school-shop/${schoolSlug}/product/${variation.id}`
      + (shopSlug ? `?shop=${encodeURIComponent(shopSlug)}` : '')
    ),
  };
};

// A package card for the bottom "Packages" grid: the package's image, name and
// price. Clicking opens the package configurator (which reads the school/shop
// it should price against from the persisted school selection).
const packageToCardItem = (pkg, schoolSlug, shopSlug, navOrPopup) => ({
  id: `pkg-${pkg.id}`,
  image: pkg.image || undefined,
  imageAlt: pkg.name,
  title: pkg.name,
  description: productShortDescription(pkg) ?? PACKAGE_DESCRIPTION,
  price: pkg.price,
  onClick: () => navOrPopup(
    schoolSlug
      ? `/clothing/abc-se/school-shop/${schoolSlug}/product-package/${encodeURIComponent(pkg.slug)}`
        + (shopSlug ? `?shop=${encodeURIComponent(shopSlug)}` : '')
      : `/clothing/abc-se/product-package/${encodeURIComponent(pkg.slug)}`
  ),
});

// A "shop selector" card: the shop's name + a representative product image,
// no price or colors. Clicking opens that shop's combined product list.
const shopToCardItem = (shop, schoolSlug, navigate) => ({
  id: `shop-${shop.id}`,
  image: shop.image || undefined,
  imageAlt: shop.name,
  title: shop.name,
  onClick: () => navigate(`/clothing/abc-se/school-shop/${schoolSlug}/${encodeURIComponent(shop.slug)}`),
});

const SchoolShop = () => {
  const navigate = useNavigate();
  const { schoolSlug, shopSlug, groupSlug } = useParams();
  const cart = useCart(BRAND_SLUG);
  const { setSchool: setCartSchool, clearCart, school: cartSchool, itemCount } = cart;
  // Cross-school guard: landing here with items from ANOTHER school in the
  // cart requires an explicit "erase & continue" before anything is touched.
  const [schoolConflict, setSchoolConflict] = useState(null);

  const { schools } = useSchools(BRAND_SLUG);

  // Only fetch when the URL provides a school slug.
  const className = useSchoolClass(BRAND_SLUG);
  const { deadlineDisplay } = useBrandSettings(BRAND_SLUG);
  const { school, shop, group: currentGroup, menuItemImages, variations, packages: schoolPackages, shops, layout, nextShopClosesAt, nextShopClosesAtDisplay, shopClosed, loading, error } = useSchoolVariations(
    schoolSlug ? BRAND_SLUG : null,
    schoolSlug,
    className,
    shopSlug,
    groupSlug,
  );
  // First load: the brand loader covers header + footer until the grid is ready
  // (later refetches — class / group switch — use the inline Preloader below).
  usePageLoader(Boolean(schoolSlug) && loading, { brand: 'ABC', label: 'Laddar produkter…' });


  /* School-root URL + exactly ONE open shop → hop to the shop-slugged URL
   * (replace:true). The cart then binds that shop, so shop-scoped context —
   * e.g. the invoice-fee override shown in checkout — matches what checkout
   * charges, and both URL forms behave identically. Multi-shop schools keep
   * the root view. The query string (?class= links) is preserved. */
  useEffect(() => {
    if (!schoolSlug || shopSlug || !schools?.length) return;
    const match = schools.find((s) => s.slug === schoolSlug);
    const openShops = match?.seasons || [];
    if (openShops.length === 1 && openShops[0]?.slug) {
      // Straight into the shop's first group when it has groups (the root is
      // never a link target on a grouped shop), else the shop root.
      const entryGroup = pickGroup(openShops[0]);
      navigate(
        `/clothing/abc-se/school-shop/${schoolSlug}/${encodeURIComponent(openShops[0].slug)}${entryGroup ? `/${encodeURIComponent(entryGroup.slug)}` : ''}${window.location.search}`,
        { replace: true },
      );
    }
  }, [schoolSlug, shopSlug, schools, navigate]);

  // ── Authoritative school sync ──
  // Visiting /school-shop/<slug> is the single source of truth for which
  // school the cart belongs to. If it differs from the cart's current
  // school, wipe the cart first then push the new school to the BE.
  useEffect(() => {
    if (!school?.id || !school?.slug) return;
    const stored = getSchoolSelection();
    // Coerce ids before comparing: the schools API serializes ids as STRINGS
    // while the cart API returns NUMBERS — a strict mismatch here made every
    // shop visit look like a school change once the cart had a school bound,
    // so clearCart() wiped the items on returning from a product page.
    const cartSchoolId = cartSchool?.id != null ? String(cartSchool.id) : null;
    const urlSchoolId = String(school.id);
    const sameAsCart = cartSchoolId === urlSchoolId;
    const sameAsLocal = String(stored?.school?.id ?? '') === urlSchoolId;
    const localClass = sameAsLocal ? (stored?.class ?? null) : null;
    const isSchoolChange = cartSchoolId != null && !sameAsCart;
    // Different school + items in the cart → don't touch anything (storage or
    // cart) until the customer confirms via the SchoolSwitchPopup.
    if (isSchoolChange && itemCount > 0) {
      setSchoolConflict((cur) => cur ?? { school, shop, localClass });
      return;
    }

    setSchoolSelection({
      school: { id: school.id, slug: school.slug, name: school.name, city: school.city },
      class: localClass,
    });

    // Sync school AND class to the BE cart so orders carry them (the BE
    // derives season and shop from the school at checkout).
    if (!sameAsCart || (localClass && localClass !== (cartSchool?.class ?? null))) {
      (async () => {
        if (isSchoolChange) {
          try { await clearCart(); } catch { /* noop */ }
        }
        // On a shop-specific URL, also bind the resolved shop to the cart.
        try { await setCartSchool({ school_id: school.id, shop_id: shop?.id ? Number(shop.id) : null, school_class: localClass }); } catch { /* noop */ }
      })();
    }
    // We deliberately depend only on school identity + shop + cart school/class.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school?.id, school?.slug, shop?.id, cartSchool?.id, cartSchool?.class, itemCount]);

  // Cross-school guard resolution: "Back" returns to the shop the cart
  // belongs to; the red confirm erases the cart and continues here.
  const handleSwitchBack = useCallback(() => {
    setSchoolConflict(null);
    navigate(cartSchool?.slug
      ? `/clothing/abc-se/school-shop/${cartSchool.slug}`
      : '/clothing/abc-se');
  }, [navigate, cartSchool?.slug]);

  const handleSwitchConfirm = useCallback(async () => {
    if (!schoolConflict) return;
    const { school: confirmSchool, shop: confirmShop, localClass } = schoolConflict;
    setSchoolSelection({
      school: { id: confirmSchool.id, slug: confirmSchool.slug, name: confirmSchool.name, city: confirmSchool.city },
      class: localClass,
    });
    try { await clearCart(); } catch { /* noop */ }
    try { await setCartSchool({ school_id: confirmSchool.id, shop_id: confirmShop?.id ? Number(confirmShop.id) : null, school_class: localClass }); } catch { /* noop */ }
    setSchoolConflict(null);
  }, [schoolConflict, clearCart, setCartSchool]);

  const navOrPopup = (path) => {
    if (localStorage.getItem(SCHOOL_KEY)) {
      navigate(path);
    } else {
      document.dispatchEvent(new CustomEvent('open-school-selector', { detail: { navigateTo: path } }));
    }
  };

  useEffect(() => {
    const prev = document.documentElement.getAttribute('data-brand');
    document.documentElement.setAttribute('data-brand', 'ABC');
    return () => {
      if (prev) document.documentElement.setAttribute('data-brand', prev);
      else document.documentElement.removeAttribute('data-brand');
    };
  }, []);

  const liveItems = useMemo(
    () => variations.map((v) => variationToCardItem(v, schoolSlug, shopSlug, navigate)),
    [variations, schoolSlug, shopSlug, navigate],
  );
  // Shop-selector mode: only on the whole-school root view (no shop chosen yet).
  const isShopSelector = !shopSlug && layout === 'shop_selector';
  const shopSelectorItems = useMemo(
    () => (shops ?? []).map((s) => shopToCardItem(s, schoolSlug, navigate)),
    [shops, schoolSlug, navigate],
  );
  const gridItems = isShopSelector ? shopSelectorItems : liveItems;
  // School/shop-scoped packages for the bottom grid (school shop pages only).
  const schoolPackageItems = useMemo(
    () => (schoolPackages ?? []).map((p) => packageToCardItem(p, schoolSlug, shopSlug, navOrPopup)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [schoolPackages, schoolSlug, shopSlug],
  );

  // Redundant bare /school-shop \u2192 stored school's shop, else brand landing.
  // (ABC-SE persists no season, so it lands on the whole-school shop.)
  if (!schoolSlug) {
    const storedSchoolSlug = getSchoolSelection()?.school?.slug;
    return storedSchoolSlug
      ? <Navigate replace to={`/clothing/abc-se/school-shop/${storedSchoolSlug}`} />
      : <Navigate replace to="/clothing/abc-se" />;
  }

  const { tag: bannerTag, title: bannerTitle, menuItemKey: bannerMenuKey } = schoolShopBanner({
    schools, schoolSlug, shopSlug, currentShop: shop, currentGroup, school, className,
  });
  // Brand-uploaded header photo for the shop's menu item, else the static map.
  const bannerImages = resolveHeaderImages({ menuItemImages, menuItemKey: bannerMenuKey, fallback: BANNER_IMAGES });
  const bannerImagesResolved = Object.keys(bannerImages).length ? bannerImages : SKOLMERCH_BANNER;

  // The requested shop's date window has passed (BE `shopClosed` marker):
  // the direct URL renders the localized closed page instead of products.
  // Unknown / removed group slug (BE 404 on ?group=): fall back to the shop
  // root rather than showing an error page for a stale link.
  if (groupSlug && shopSlug && schoolSlug && !loading && error) {
    return <Navigate replace to={`/clothing/abc-se/school-shop/${schoolSlug}/${shopSlug}`} />;
  }

  if (shopClosed) {
    return (
      <>
        <Header />
        <ClosedShopMessage brandSlug="abcse" feSlug="abc-se" standalone />
        <Footer />
      </>
    );
  }

  return (
    <div className="school-shop">
      <Header />

      <SecondaryBanner
        tag={bannerTag}
        title={bannerTitle}
        {...bannerImagesResolved}
        trustpilot={ABC_TRUSTPILOT}
      />

      <main className="school-shop__wrapper">
        {deadlineDisplay !== 'none' && nextShopClosesAt && (
          <div className="school-shop__fomo-container">
            <FomoBlock
              closesAt={nextShopClosesAt}
              template={deadlineDisplay === 'date' ? FOMO_DATE_TEMPLATE : FOMO_COUNTDOWN_TEMPLATE}
              units={FOMO_UNITS}
              staticTime={deadlineDisplay === 'date' ? (nextShopClosesAtDisplay || undefined) : undefined}
            />
          </div>
        )}

        {/* ── Products Widget 1: Alla Produkter ── */}
        {schoolSlug ? (
          <>
            {(loading || error || gridItems.length === 0) ? (
              loading ? (
                <div style={{ padding: '4rem 2rem', textAlign: 'center' }}>
                  <Preloader label="Laddar produkter…" />
                </div>
              ) : error ? (
                <ShopEmptyState
                  variant="error"
                  feSlug="abc-se"
                  title="Produkterna kunde inte laddas"
                  text="Prova att ladda om sidan. Om problemet kvarstår hjälper vi gärna till — se vår FAQ eller kontakta oss direkt."
                  contactLabel="Kontakta oss"
                  faqLabel="Se FAQ"
                />
              ) : (
                <ShopEmptyState
                  feSlug="abc-se"
                  title="Inga produkter just nu"
                  text="Butiken kanske inte har öppnat ännu, eller så uppdateras produkterna. Har du frågor hjälper vi gärna till."
                  contactLabel="Kontakta oss"
                  faqLabel="Se FAQ"
                />
              )
            ) : (
              <ProductsGrid
                moreLabel="fler"
                paddingTop="medium-l"
                paddingBottom="medium-s"
                background="var(--color-gray-0)"
                ctaIcon={<ArrowRight size={20} />}
                ctaOnClick={() => document.dispatchEvent(new CustomEvent('open-school-selector'))}
                className={isShopSelector ? 'school-shop__shop-cards' : undefined}
                items={gridItems}
              />
            )}
          </>
        ) : (
          <ProductsGrid
            moreLabel="fler"
            paddingTop="medium-l"
            paddingBottom="medium-s"
            background="var(--color-gray-0)"
            ctaIcon={<ArrowRight size={20} />}
            ctaOnClick={() => document.dispatchEvent(new CustomEvent('open-school-selector'))}
            items={[
              { id: 1, image: require('../../../../assets/abc-se/products/product-1.jpg'), imageAlt: 'HOODIE NAVY', title: 'HOODIE NAVY', price: '159,00 KR', colors: ['#2e2e52', '#afafaf', '#080c0a'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 2, image: require('../../../../assets/abc-se/products/product-2.jpg'), imageAlt: 'HOODIE GREY', title: 'HOODIE GREY', price: '159,00 KR', colors: ['#2e2e52', '#afafaf', '#080c0a'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 3, image: require('../../../../assets/abc-se/products/product-3.jpg'), imageAlt: 'CREWNECK NAVY', title: 'CREWNECK NAVY', price: '159,00 KR', colors: ['#2e2e52', '#afafaf'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 4, image: require('../../../../assets/abc-se/products/product-4.jpg'), imageAlt: 'CREWNECK GREY', title: 'CREWNECK GREY', price: '159,00 KR', colors: ['#2e2e52', '#afafaf'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 5, image: require('../../../../assets/abc-se/products/product-5.jpg'), imageAlt: 'T-SHIRT UNISEX', title: 'T-SHIRT UNISEX', price: '99,00 KR', colors: ['#6f6f6f', '#2f2f2f', '#aad1ce', '#aad1b2', '#dccee1', '#ea9fa0', '#3d88dc', '#c4c4c4'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 6, image: require('../../../../assets/abc-se/products/product-6.jpg'), imageAlt: 'T-SHIRT WOMENS', title: 'T-SHIRT WOMENS', price: '99,00 KR', colors: ['#6f6f6f', '#2f2f2f', '#aad1ce'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 7, image: require('../../../../assets/abc-se/products/product-7.jpg'), imageAlt: 'ZIP HOODIE', title: 'ZIP HOODIE', price: '189,00 KR', colors: ['#6f6f6f', '#2f2f2f', '#aad1ce', '#aad1b2', '#dccee1'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 8, image: require('../../../../assets/abc-se/products/product-8.jpg'), imageAlt: 'SWEATPANTS', title: 'SWEATPANTS', price: '139,00 KR', colors: ['#6f6f6f', '#2f2f2f', '#aad1ce'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 9, image: require('../../../../assets/abc-se/products/product-9.jpg'), imageAlt: 'HOODIE & SWEATPANTS', title: 'HOODIE & SWEATPANTS', price: '299,00 KR', colors: ['#080c0a', '#293a35', '#28e280'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 10, image: require('../../../../assets/abc-se/products/product-10.jpg'), imageAlt: 'FESTIVAL PAKKE', title: 'FESTIVAL PAKKE', price: '399,00 KR', colors: ['#080c0a', '#ffffff'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 11, image: require('../../../../assets/abc-se/products/product-11.jpg'), imageAlt: 'HOODIE & T-SHIRT', title: 'HOODIE & T-SHIRT', price: '249,00 KR', colors: ['#080c0a', '#6b1d2e', '#1e2a4a'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
              { id: 12, image: require('../../../../assets/abc-se/products/product-12.jpg'), imageAlt: 'KOMPLETT PAKET', title: 'KOMPLETT PAKET', price: '499,00 KR', colors: ['#080c0a', '#293a35'], onClick: () => navOrPopup('/clothing/abc-se/product-details') },
            ]}
          />
        )}

        {/* ── Fullwidth Content Widget (hidden in shop-selector mode) ── */}
        {!isShopSelector && (
        <Fullwidth
          showImage={false}
          subtitle="50.000+ ELEVER VÄLJER OSS VARJE ÅR"
          mainCopyVariant="h2"
          mainCopy="Sveriges skarpaste priser med prisgaranti, snabb leverans och attraktiva paketlösningar."
          additionalCopy="Beställ tryggt och säkert med över 20 års erfarenhet – och."
          tags={['A4 ryggtryck', '10 × 10 cm bröstlogo', 'Gratis frakt', 'Enkel individuell betalning', 'Fritt val av produkter']}
          showHyperlink={false}
        />
        )}

        {/* ── Products Widget 2: school/shop-scoped Paketlösningar (hidden in shop-selector mode) ── */}
        {!isShopSelector && schoolSlug && !loading && schoolPackageItems.length > 0 && (
          <ProductsGrid
            moreLabel="fler"
            background="var(--color-gray-0)"
            paddingTop="medium-m"
            paddingBottom="large-l"
            ctaOnClick={() => document.dispatchEvent(new CustomEvent('open-school-selector'))}
            items={schoolPackageItems}
          />
        )}

        {/* ── Products Widget 2: Paketlösningar — kept as demo for now ── */}
        {!schoolSlug && (
          <ProductsGrid
            moreLabel="fler"
            background="var(--color-gray-0)"
            paddingTop="medium-m"
            paddingBottom="large-l"
            ctaOnClick={() => document.dispatchEvent(new CustomEvent('open-school-selector'))}
            items={[
              { id: 13, image: require('../../../../assets/abc-se/products/product-9.jpg'), imageAlt: 'HOODIE & SWEATPANTS PAKET', title: 'HOODIE & SWEATPANTS PAKET', description: 'Paketlösning', price: '299,00 KR', colors: ['#080c0a', '#293a35', '#28e280'], onClick: () => navOrPopup('/clothing/abc-se/product-package') },
              { id: 14, image: require('../../../../assets/abc-se/products/product-10.jpg'), imageAlt: 'FESTIVAL PAKET', title: 'FESTIVAL PAKET', description: 'Paketlösning', price: '399,00 KR', colors: ['#080c0a', '#ffffff'], onClick: () => navOrPopup('/clothing/abc-se/product-package') },
              { id: 15, image: require('../../../../assets/abc-se/products/product-11.jpg'), imageAlt: 'HOODIE & T-SHIRT PAKET', title: 'HOODIE & T-SHIRT PAKET', description: 'Paketlösning', price: '249,00 KR', colors: ['#080c0a', '#6b1d2e', '#1e2a4a'], onClick: () => navOrPopup('/clothing/abc-se/product-package') },
              { id: 16, image: require('../../../../assets/abc-se/products/product-12.jpg'), imageAlt: 'KOMPLETT PAKET', title: 'KOMPLETT PAKET', description: 'Paketlösning', price: '499,00 KR', colors: ['#080c0a', '#293a35'], onClick: () => navOrPopup('/clothing/abc-se/product-package') },
              { id: 17, image: require('../../../../assets/abc-se/products/product-9.jpg'), imageAlt: 'HOODIE & SWEATPANTS PAKET', title: 'HOODIE & SWEATPANTS PAKET', description: 'Paketlösning', price: '299,00 KR', colors: ['#080c0a', '#293a35', '#28e280'], onClick: () => navOrPopup('/clothing/abc-se/product-package') },
              { id: 18, image: require('../../../../assets/abc-se/products/product-10.jpg'), imageAlt: 'FESTIVAL PAKET', title: 'FESTIVAL PAKET', description: 'Paketlösning', price: '399,00 KR', colors: ['#080c0a', '#ffffff'], onClick: () => navOrPopup('/clothing/abc-se/product-package') },
              { id: 19, image: require('../../../../assets/abc-se/products/product-11.jpg'), imageAlt: 'HOODIE & T-SHIRT PAKET', title: 'HOODIE & T-SHIRT PAKET', description: 'Paketlösning', price: '249,00 KR', colors: ['#080c0a', '#6b1d2e', '#1e2a4a'], onClick: () => navOrPopup('/clothing/abc-se/product-package') },
              { id: 20, image: require('../../../../assets/abc-se/products/product-12.jpg'), imageAlt: 'KOMPLETT PAKET', title: 'KOMPLETT PAKET', description: 'Paketlösning', price: '499,00 KR', colors: ['#080c0a', '#293a35'], onClick: () => navOrPopup('/clothing/abc-se/product-package') },
            ]}
          />
        )}
      </main>

      <SchoolSwitchPopup
        isOpen={!!schoolConflict}
        brandSlug={BRAND_SLUG}
        onBack={handleSwitchBack}
        onConfirm={handleSwitchConfirm}
      />

      <Footer />
    </div>
  );
};

export default SchoolShop;
